import { useState } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Lock, Loader2, Eye, EyeOff, CheckCircle } from 'lucide-react';

export function SecurityTab() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) { toast.error('New passwords do not match'); return; }
    if (newPassword.length < 8 || !/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/\d/.test(newPassword)) {
      toast.error('Password must be at least 8 characters with uppercase, lowercase, and a number');
      return;
    }
    setSavingPassword(true);
    try {
      await api.changePassword({ current_password: currentPassword, new_password: newPassword });
      toast.success('Password changed successfully');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
    setSavingPassword(false);
  };

  return (
    <div className="bg-surface border border-[#262626] rounded-xl p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-1">Change Password</h2>
        <p className="text-sm text-[#A1A1AA]">Keep your account secure with a strong password</p>
      </div>

      <div className="space-y-4 max-w-md">
        <div>
          <Label htmlFor="currentPw" className="mb-2 block">Current Password</Label>
          <div className="relative">
            <Input id="currentPw" type={showCurrentPw ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter current password" className="bg-[#111] border-[#262626] pr-10" data-testid="input-current-password" />
            <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555] hover:text-white">
              {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <Label htmlFor="newPw" className="mb-2 block">New Password</Label>
          <div className="relative">
            <Input id="newPw" type={showNewPw ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password (min 8 chars, upper+lower+number)" className="bg-[#111] border-[#262626] pr-10" data-testid="input-new-password" />
            <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555] hover:text-white">
              {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <Label htmlFor="confirmPw" className="mb-2 block">Confirm New Password</Label>
          <Input id="confirmPw" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className="bg-[#111] border-[#262626]" data-testid="input-confirm-password" />
        </div>
      </div>

      <Button onClick={handleChangePassword} disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword} className="bg-lime text-black hover:bg-lime/90 font-bold" data-testid="change-password-btn">
        {savingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
        Change Password
      </Button>
    </div>
  );
}
